import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowRight, Check, ChevronRight, Infinity, Settings2, Sparkles, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import PageLayout from "@/components/layout/PageLayout";
import { ModulePicker } from "@/components/modules/ModulePicker";
import { CoreIncluded } from "@/components/modules/CoreIncluded";
import { plans, type PlanDefinition, formatPoints, recommendPlan } from "@/data/plans";
import { modules, calcUsedPoints } from "@/data/modules";
import { getPresetById, getPresetPlan } from "@/data/presets";
import { stripePlans, type StripePlanId } from "@/data/stripe-plans";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { pushDataLayer } from "@/lib/gtm";

// ── Plan selector mini-card ──────────────────────────────────
function PlanSelectCard({
  plan,
  selected,
  usedPoints,
  onSelect,
}: {
  plan: PlanDefinition;
  selected: boolean;
  usedPoints: number;
  onSelect: (id: string) => void;
}) {
  const isUnlimited = plan.pointsIncluded === null;
  const fits = isUnlimited || (plan.pointsIncluded !== null && usedPoints <= plan.pointsIncluded);

  return (
    <button
      type="button"
      onClick={() => onSelect(plan.id)}
      aria-pressed={selected}
      className={cn(
        "group relative w-full text-left rounded-xl border p-4 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        selected
          ? "border-primary bg-primary/5 shadow-card-hover"
          : "border-border bg-card hover:border-primary/40 hover:shadow-card",
      )}
    >
      {plan.highlight && (
        <span className="absolute -top-2.5 left-4 gradient-accent inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white">
          Beliebtester
        </span>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm text-foreground">{plan.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{plan.tagline}</p>
        </div>

        <span
          className={cn(
            "shrink-0 inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-bold",
            isUnlimited ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary",
          )}
        >
          {isUnlimited ? <Infinity className="h-3 w-3" /> : plan.pointsIncluded}
          {!isUnlimited && <span className="font-normal text-[10px]">Pkt</span>}
        </span>
      </div>

      <div className={cn("mt-2 text-[11px] font-medium", fits ? "text-success" : "text-destructive")}>
        {fits ? "✓ Passt zu Ihrer Auswahl" : `✗ ${usedPoints} Punkte benötigt`}
      </div>

      <span
        className={cn(
          "absolute top-3 right-3 h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors",
          selected ? "border-primary bg-primary" : "border-muted-foreground/30 group-hover:border-primary/50",
        )}
      >
        {selected && <Check className="h-2.5 w-2.5 text-primary-foreground" strokeWidth={3} />}
      </span>
    </button>
  );
}

// ── Summary chip ─────────────────────────────────────────────
function ModuleChip({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary border border-primary/20">
      {name}
      <button
        type="button"
        onClick={onRemove}
        className="hover:text-destructive transition-colors"
        aria-label={`${name} entfernen`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function Configurator() {
  const summaryRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  const [selectedPlanId, setSelectedPlanId] = useState<string>("professional");
  const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);

  // ── Prefill from URL params (preset or redirect back from login) ──
  useEffect(() => {
    // From login redirect: ?plan=starter&modules=mod1,mod2
    const planParam = searchParams.get("plan");
    const modulesParam = searchParams.get("modules");
    if (planParam) setSelectedPlanId(planParam);
    if (modulesParam) setSelectedModuleIds(modulesParam.split(",").filter(Boolean));

    // From preset: ?preset=<id>
    const presetId = searchParams.get("preset");
    if (!presetId) return;
    const preset = getPresetById(presetId);
    if (!preset) return;
    setSelectedModuleIds(preset.moduleIds);
    const bestPlan = getPresetPlan(preset);
    setSelectedPlanId(bestPlan.id);
  }, [searchParams]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) ?? plans[1],
    [selectedPlanId],
  );

  const usedPoints = useMemo(() => calcUsedPoints(selectedModuleIds), [selectedModuleIds]);

  const availablePoints: number | null = selectedPlan.pointsIncluded;

  const handleToggle = useCallback((id: string) => {
    setSelectedModuleIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  }, []);

  const selectedModules = useMemo(
    () => modules.filter((m) => selectedModuleIds.includes(m.id)),
    [selectedModuleIds],
  );

  const scrollToSummary = () => {
    summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const priceUnknown = selectedPlan.priceMonthly === null;

  return (
    <PageLayout>
      <Helmet>
        <title>ERP-Konfigurator – Module live zusammenstellen</title>
        <meta name="description" content="Wählen Sie Rechnungs-, CRM-, Lager- und Personal-Module per Punkte-Budget. Sofort den passenden NextInvoice-Plan sehen." />
        <link rel="canonical" href="https://nxtinvoice.de/konfigurator" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "NextInvoice Modul-Konfigurator",
          "url": "https://nxtinvoice.de/konfigurator",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Stellen Sie sich Ihr individuelles NextInvoice-Setup aus Modulen zusammen."
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://nxtinvoice.de/" },
            { "@type": "ListItem", "position": 2, "name": "Konfigurator" }
          ]
        })}</script>
      </Helmet>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative gradient-hero pattern-grid overflow-hidden">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-primary/8 blur-3xl" />
        <div className="pointer-events-none absolute top-20 right-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />

        <div className="container relative py-20 lg:py-28 text-center">
          <Badge
            variant="secondary"
            className="mb-6 inline-flex items-center gap-2 border border-primary/20 bg-primary/8 px-4 py-1.5 text-sm font-semibold text-primary"
          >
            <Settings2 className="h-4 w-4" />
            Modul-Konfigurator
          </Badge>

          <h1 className="font-display text-4xl font-bold tracking-tight text-foreground lg:text-5xl xl:text-6xl text-balance mx-auto max-w-3xl">
            Stellen Sie sich Ihr System aus Modulen zusammen.
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground text-balance">
            Wählen Sie einen Plan mit Punkte-Budget und klicken Sie Ihre Module zusammen.{" "}
            <span className="font-semibold text-foreground">Core ist immer dabei.</span>
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button
              onClick={scrollToSummary}
              size="lg"
              className="group h-12 bg-accent px-8 text-accent-foreground shadow-lg shadow-accent/25 hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/30"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Zum Ergebnis
              <ArrowDown className="ml-2 h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </Button>
            <Button variant="ghost" size="lg" className="h-12 gap-2" asChild>
              <Link to="/preise">
                Preise ansehen <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Main configurator layout ─────────────────────────── */}
      <section className="container py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-8 items-start">

          {/* ── LEFT: Plan selector ─────────────────────────── */}
          <aside className="lg:sticky lg:top-28 flex flex-col gap-6">
            <div className="rounded-2xl border border-border bg-card shadow-card p-6">
              <h2 className="font-display font-bold text-lg text-foreground mb-1">
                1. Plan wählen
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                Der Plan bestimmt Ihr Punkte-Budget für Module.
              </p>

              <div className="flex flex-col gap-3">
                {plans.map((plan) => (
                  <PlanSelectCard
                    key={plan.id}
                    plan={plan}
                    selected={selectedPlanId === plan.id}
                    usedPoints={usedPoints}
                    onSelect={setSelectedPlanId}
                  />
                ))}
              </div>
            </div>

            <CoreIncluded className="hidden lg:block" compact />
          </aside>

          {/* ── RIGHT: Module picker ────────────────────────── */}
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-border bg-card shadow-card p-6">
              <h2 className="font-display font-bold text-lg text-foreground mb-1">
                2. Module wählen
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Jedes Modul kostet Punkte aus Ihrem Plan-Budget. Core ist in jedem Plan inklusive.
              </p>

              <ModulePicker
                selectedIds={selectedModuleIds}
                onToggle={handleToggle}
                availablePoints={availablePoints}
                showCategories={true}
              />
            </div>

            <CoreIncluded className="lg:hidden" />
          </div>
        </div>
      </section>

      {/* ── Summary panel ────────────────────────────────────── */}
      <section ref={summaryRef} className="container pb-20 scroll-mt-24">
        <div className="relative overflow-hidden rounded-3xl border border-primary/20 bg-card shadow-card-hover">
          <div className="pointer-events-none absolute inset-0 gradient-hero opacity-40" />
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border">

            {/* Left: Configuration overview */}
            <div className="p-8 lg:p-10 flex flex-col gap-6">
              <div>
                <h2 className="font-display font-bold text-2xl text-foreground mb-2">
                  Ihre Konfiguration
                </h2>
                <p className="text-sm text-muted-foreground">
                  Zusammenfassung Ihrer gewählten Einstellungen.
                </p>
              </div>

              <div className="flex items-center gap-3 rounded-xl bg-primary/8 border border-primary/15 px-4 py-3">
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
                  <Check className="h-4 w-4 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Gewählter Plan</p>
                  <p className="font-display font-bold text-foreground">{selectedPlan.name}</p>
                </div>
                <span className="text-sm font-semibold text-primary">
                  {formatPoints(selectedPlan.pointsIncluded)}
                </span>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Core (immer inklusive)
                </p>
                <div className="flex flex-wrap gap-2">
                  {["Dokumentenmanagement", "E-Mail & Versandkanal", "Benutzer & Berechtigungen"].map((core) => (
                    <span
                      key={core}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-success/10 px-2.5 py-1 text-xs font-medium text-success border border-success/20"
                    >
                      <Check className="h-3 w-3" />
                      {core}
                    </span>
                  ))}
                </div>
              </div>

              <Separator />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Gewählte Module
                  </p>
                  <span
                    className={cn(
                      "text-sm font-bold tabular-nums",
                      availablePoints !== null && usedPoints > availablePoints
                        ? "text-destructive"
                        : "text-primary",
                    )}
                  >
                    {usedPoints} / {availablePoints !== null ? availablePoints : "∞"} Punkte
                  </span>
                </div>

                {selectedModules.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">
                    Noch keine Module ausgewählt – klicken Sie oben auf Module.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedModules.map((m) => (
                      <ModuleChip
                        key={m.id}
                        name={m.name}
                        onRemove={() => handleToggle(m.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: CTA */}
            <div className="p-8 lg:p-10 flex flex-col justify-center gap-6">
              <div>
                <h3 className="font-display font-bold text-xl text-foreground mb-2">
                  Bereit zum Starten?
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {subscription?.subscribed ? (
                    "Konfigurieren Sie Ihre Module und wechseln Sie Ihren Plan."
                  ) : (
                    <>
                      Preise und Buchung folgen im nächsten Schritt. Testen Sie NextInvoice
                      jetzt{" "}
                      <span className="font-semibold text-foreground">1 Monat kostenlos</span>{" "}
                      – keine Kreditkarte erforderlich.
                    </>
                  )}
                </p>
              </div>

              <div className="rounded-xl border border-border bg-background/60 p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Plan</p>
                  <p className="font-display font-bold text-foreground">{selectedPlan.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{selectedPlan.tagline}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Module</p>
                  <p className="font-bold text-2xl text-foreground">{selectedModules.length}</p>
                  <p className="text-xs text-muted-foreground">{usedPoints} Punkte</p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {!subscription?.subscribed && (
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="agb-checkout"
                      checked={agbAccepted}
                      onCheckedChange={(checked) => setAgbAccepted(checked === true)}
                      className="mt-0.5"
                    />
                    <label htmlFor="agb-checkout" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                      Ich akzeptiere die{" "}
                      <Link to="/agb" target="_blank" className="text-primary hover:underline">AGB</Link>
                      {" "}und die{" "}
                      <Link to="/datenschutz" target="_blank" className="text-primary hover:underline">Datenschutzerklärung</Link>.
                    </label>
                  </div>
                )}
                <Button
                  size="lg"
                  className="group h-14 w-full bg-accent px-8 text-base font-bold text-accent-foreground shadow-lg shadow-accent/25 hover:bg-accent/90 hover:shadow-xl hover:shadow-accent/30 relative overflow-hidden"
                  disabled={checkoutLoading || (!subscription?.subscribed && !agbAccepted)}
                  onClick={async () => {
                    if (!user) {
                      const returnUrl = `/konfigurator?plan=${selectedPlanId}&modules=${selectedModuleIds.join(",")}`;
                      navigate(`/portal/login?redirect=${encodeURIComponent(returnUrl)}`);
                      return;
                    }
                    setCheckoutLoading(true);
                    pushDataLayer("begin_checkout", {
                      plan: selectedPlanId,
                      modules: selectedModuleIds,
                      module_count: selectedModuleIds.length,
                      points_used: usedPoints,
                    });
                    try {
                      const planKey = selectedPlanId as StripePlanId;
                      const plan = stripePlans[planKey];
                      if (!plan) throw new Error("Plan nicht gefunden");
                      const priceId = plan.monthly_price_id;
                      const { data, error } = await supabase.functions.invoke("create-checkout", {
                        body: { priceId, moduleIds: selectedModuleIds },
                      });
                      if (error) throw error;
                      if (data?.url) {
                        window.location.href = data.url;
                      }
                    } catch (e) {
                      console.error("Checkout error:", e);
                      toast.error("Checkout konnte nicht gestartet werden.");
                    } finally {
                      setCheckoutLoading(false);
                    }
                  }}
                >
                    <Sparkles className="mr-2 h-5 w-5" />
                    {checkoutLoading ? "Wird geladen…" : subscription?.subscribed ? "Plan wechseln" : "1 Monat kostenlos testen"}
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                </Button>
                <Button variant="outline" size="lg" className="h-12 w-full gap-2" asChild>
                  <Link to="/preise">
                    Preise ansehen
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="pt-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  {selectedPlan.name} empfohlen für
                </p>
                <ul className="flex flex-col gap-1.5">
                  {selectedPlan.recommendedFor.map((text) => (
                    <li key={text} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-success shrink-0" />
                      <span className="text-foreground">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
